import { TypedDocumentNode, gql, useSuspenseQuery } from '@apollo/client';
import { Suspense, useMemo } from 'react';
import { Navigate, generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { Position } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { PvEImplementation } from '@sorare/core/src/components/Minigames/PvEImplementation';
import {
  FOOTBALL,
  FOOTBALL_MINIGAMES_PVE_LEAGUE,
} from '@sorare/core/src/constants/__generated__/routes';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { isType } from '@sorare/core/src/gql';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';
import { randomIntFromInterval } from '@sorare/core/src/lib/math';

import { positionShortNames } from '@sorare/gameplay/src/lib/so5';

import laligaEASports from 'assets/minigames/laligaEASports.png';
import laligaSorare from 'assets/minigames/laligaSorare.png';
import popupBackground from 'assets/minigames/popupBackground.jpg';
import {
  BUNDESLIGA_PLAYERS,
  LALIGA_PLAYERS,
  PL_PLAYERS,
} from 'pages/minigames/pve_/PLAYERS';

import { PVEQuery, PVEQueryVariables } from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

export { popupBackground };

type Card = PVEQuery['anyCards'][number] & {
  __typename: 'Card';
  maskUrl?: string;
};

const competitions = {
  PremierLeague: {
    fieldUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-england/stadium-in_season.jpg',
  },
  LALIGA: {
    fieldUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-spain/stadium-in_season.jpg',
  },
  Bundesliga: {
    fieldUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-germany/stadium-in_season.jpg',
  },
};

const randomIntExcept = (min: number, max: number, excepts: number[]) => {
  let result;
  do {
    result = Math.floor(Math.random() * (max - min + 1) + min);
  } while (excepts.includes(result));
  return result;
};

const POSITION = ['GK', 'DEF', 'MD', 'FW', 'EX'] as const;

const LEAGUES_DATA_MAP = {
  epl: {
    data: PL_PLAYERS,
    title: 'Premier League',
  },
  laliga: {
    data: LALIGA_PLAYERS,
    title: 'LALIGA',
  },
  bundesliga: {
    data: BUNDESLIGA_PLAYERS,
    title: 'Bundesliga',
  },
};

export const buildData = (cards: ReadonlyArray<Card>) => {
  const pickedPlayers: number[] = [];
  const data: [Card, Card][] = [];

  const GK = cards.filter(card =>
    card.anyPositions?.includes(Position.Goalkeeper)
  );
  const DEF = cards.filter(card =>
    card.anyPositions?.includes(Position.Defender)
  );
  const MD = cards.filter(card =>
    card.anyPositions?.includes(Position.Midfielder)
  );
  const FW = cards.filter(card =>
    card.anyPositions?.includes(Position.Forward)
  );

  const playersData = { GK, DEF, MD, FW };

  const allPlayers = [...GK, ...DEF, ...MD, ...FW] as const;

  POSITION.forEach((pos, i) => {
    if (pos !== 'EX') {
      const playersLength = playersData[pos].length;
      const firstPlayerIndex = randomIntFromInterval(0, playersLength - 1);
      const secondPlayerIndex = randomIntExcept(0, playersLength - 1, [
        firstPlayerIndex,
      ]);
      const positionsBefore = POSITION.slice(0, i);
      const beginningIndex = positionsBefore.reduce(
        // @ts-expect-error EX is not a possible value here
        (acc, cur) => acc + playersData[cur].length,
        0
      );
      const finalFirstPlayerIndex = beginningIndex + firstPlayerIndex;
      const finalSecondPlayerIndex = beginningIndex + secondPlayerIndex;
      data.push([
        allPlayers[finalFirstPlayerIndex],
        allPlayers[finalSecondPlayerIndex],
      ]);
      pickedPlayers.push(finalFirstPlayerIndex, finalSecondPlayerIndex);
    } else {
      const firstPlayerIndex = randomIntExcept(
        playersData.GK.length,
        allPlayers.length - 1,
        pickedPlayers
      );
      const secondPlayerIndex = randomIntExcept(
        playersData.GK.length,
        allPlayers.length - 1,
        [firstPlayerIndex, ...pickedPlayers]
      );
      data.push([allPlayers[firstPlayerIndex], allPlayers[secondPlayerIndex]]);
    }
  });
  return data;
};

export const POSITIONS_MAP = [
  {
    position: Position.Goalkeeper,
  },
  {
    position: Position.Defender,
  },
  {
    position: Position.Midfielder,
  },
  {
    position: Position.Forward,
  },
  {
    position: Position.Unknown,
    label: positionShortNames['Extra Player'],
  },
];

const PVE_QUERY = gql`
  query PVEQuery($slugs: [String!]!) {
    anyCards(slugs: $slugs) {
      slug
      ...PvEImplementation_anyCardInterface
    }
  }
  ${PvEImplementation.fragments.anyCardInterface}
` as TypedDocumentNode<PVEQuery, PVEQueryVariables>;

const PvEPageComponent = ({
  league,
}: {
  league: keyof typeof LEAGUES_DATA_MAP;
}) => {
  const cardSlugs = LEAGUES_DATA_MAP[league].data;
  const { signUp } = useConnectionContext();
  const handleNavigate = () => signUp();
  const { data } = useSuspenseQuery(PVE_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      slugs: cardSlugs,
    },
  });

  const playersData = useMemo(
    () =>
      buildData(
        data.anyCards
          .filter(card => isType(card, 'Card'))
          .filter(e => e.score && e.score > 0)
      ),
    // regenerage random card slugs on retry
    // league is also required to trigger data regen on unknown league redirection (there might be a better way to do this)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [league]
  );

  const leagueTitle =
    LEAGUES_DATA_MAP[league as 'laliga' | 'epl' | 'bundesliga'].title;
  const normalizedLeagueTitle = leagueTitle.replace(
    /\s+/g,
    ''
  ) as keyof typeof competitions;
  const fieldUrl = competitions[normalizedLeagueTitle]?.fieldUrl;

  return (
    <PvEImplementation
      key={leagueTitle}
      title={leagueTitle}
      playersData={playersData}
      onClick={handleNavigate}
      fieldUrl={fieldUrl}
      laligaEASports={laligaEASports}
      laligaSorare={laligaSorare}
      positionsMap={POSITIONS_MAP}
      popupBackground={popupBackground}
    />
  );
};

const PvEPage = () => {
  const loggedIn = useIsLoggedIn();
  const { league } = useTypedParams<PageParams>();

  if (loggedIn) {
    return <Navigate to={FOOTBALL} />;
  }

  if (!Object.keys(LEAGUES_DATA_MAP).includes(league as string)) {
    return (
      <Navigate
        replace
        to={generatePath(FOOTBALL_MINIGAMES_PVE_LEAGUE, { league: 'epl' })}
      />
    );
  }

  return (
    <Suspense fallback={<LoadingIndicator fullScreen />}>
      <PvEPageComponent league={league as keyof typeof LEAGUES_DATA_MAP} />
    </Suspense>
  );
};

export default PvEPage;
